import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, AfterViewInit, ChangeDetectorRef, inject } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslocoService } from '@jsverse/transloco';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { DataExchangeSubRequestTypeDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-type-dto';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { ReopenOutcomeDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/reopen-outcome-dto';
import { ReopenRequestResultDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/reopen-request-result-dto';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { DataExchangeSaveToDirectoryService } from 'src/app/services/data-exchange-save-to-directory.service';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { UpdateDataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/update-data-exchange-request-status-dto';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { ComponentNavigationService } from 'src/app/navigation/services/navigation-services/component-navigation-service';
import { MaterialNavigationService } from 'src/app/navigation/services/navigation-services/material-navigation.service';
import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import { PackagingUnitNavigationService } from 'src/app/navigation/services/navigation-services/packaging-unit-navigation.service';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { DataExchangeRequestPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { getDeadlineStateFilterOptions } from 'src/app/model/row-badge-map';
import { applyRowState } from 'src/app/model/data-exchange-request-row';
import {
  SubRequestCommentsDialogComponent,
  SubRequestCommentsDialogData,
  SubRequestCommentsDialogResult,
} from 'src/app/components/dialogs/sub-request-comments-dialog/sub-request-comments-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import {
  EditDataExchangeRequestDeadlineDialogComponent,
  EditDataExchangeRequestDeadlineDialogData,
  EditDataExchangeRequestDeadlineDialogResult,
} from 'src/app/components/directory-tree/dialogs/edit-data-exchange-request-deadline-dialog/edit-data-exchange-request-deadline-dialog.component';
import { showApiErrorDialog } from 'src/app/util/api-error-util';
import {
  ReopenSubRequestsDialogComponent,
  ReopenSubRequestsDialogData,
  ReopenSubRequestsDialogResult,
} from 'src/app/components/directory-tree/dialogs/reopen-sub-requests-dialog/reopen-sub-requests-dialog.component';
import {
  ReopenDecisionDialogComponent,
  ReopenDecisionDialogData,
  ReopenDecisionDialogResult,
} from 'src/app/components/directory-tree/dialogs/reopen-decision-dialog/reopen-decision-dialog.component';

@Component({
  selector: 'app-data-exchange-request-table',
  templateUrl: './data-exchange-request-table.component.html',
  styleUrls: ['./data-exchange-request-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DataExchangeRequestTableComponent implements OnChanges, AfterViewInit {
  public readonly requestStatus = DataExchangeRequestStatusDto;

  @Input() dataSource!: PaginationDataSource<DataExchangeRequestDto>;
  @Input() isReceived = false;
  @Input() isClosed = false;
  @Input() displayedColumns: string[] = [];
  @Input() expandRequestId: number | null = null;

  @Output() dataReloadRequired = new EventEmitter<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  filteredColumns: string[] = [];
  expandedElementId: number | null = null;
  isLoading = this.dataSource?.isLoading;
  private subRequestDataSourceCache = new Map<number, PaginationDataSource<DataExchangeSubRequestDto>>();

  private readonly closedStatusValues = [DataExchangeRequestStatusDto.Closed, DataExchangeRequestStatusDto.Deleted];
  private readonly openStatusValues = [
    DataExchangeRequestStatusDto.Open,
    DataExchangeRequestStatusDto.InProgress,
    DataExchangeRequestStatusDto.Submitted,
    DataExchangeRequestStatusDto.ReOpened,
  ];
  private readonly closedStatus = this.closedStatusValues.map(x => x.toString());
  private readonly openStatus = this.openStatusValues.map(x => x.toString());

  private readonly saveToDirectoryService = inject(DataExchangeSaveToDirectoryService);
  private readonly packagingUnitNavigationService = inject(PackagingUnitNavigationService);
  private readonly packagingSystemNavigationService = inject(PackagingSystemNavigationService);
  private readonly componentNavigationService = inject(ComponentNavigationService);
  private readonly materialNavigationService = inject(MaterialNavigationService);

  constructor(
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private router: Router,
    private componentTypeService: ComponentTypeService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private cd: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.isLoading = this.dataSource.isLoading;
    this.interceptStatusFilterOptions();
    this.dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
    this.dataSource.page.subscribe(page => {
      page.items.forEach(row => applyRowState(row, this.isReceived));
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
    });
    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, [
      { fieldName: DataExchangeRequestPaginationFields.Status, values: this.isClosed ? this.closedStatus : this.openStatus },
    ]);
    this.cd.detectChanges();
  }

  private interceptStatusFilterOptions(): void {
    const originalAddFilterOptions = this.dataSource.addFilterOptions.bind(this.dataSource);
    this.dataSource.addFilterOptions = (fieldName: string, options: any[], mode: 'OR' | 'AND' = 'OR') => {
      if (fieldName !== DataExchangeRequestPaginationFields.Status) {
        originalAddFilterOptions(fieldName, options, mode);
        return;
      }

      const defaults = this.isClosed ? this.closedStatusValues : this.openStatusValues;
      const selected = options.filter(value => value !== undefined && value !== null).map(value => Number(value));
      const effective = selected.length > 0 ? defaults.filter(status => selected.includes(status)) : defaults;
      originalAddFilterOptions(fieldName, effective, mode);
    };
  }

  get expandedElement(): DataExchangeRequestDto | null {
    if (this.expandedElementId == null) {
      return null;
    }
    return this.dataSource?.data.find(r => r.id === this.expandedElementId) ?? null;
  }

  trackByRequestId = (_: number, row: DataExchangeRequestDto): number => row.id;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && !changes['dataSource'].firstChange && this.expandedElementId != null) {
      this.subRequestDataSourceCache.clear();
      if (!this.expandedElement) {
        this.expandedElementId = null;
      }
    }

    if (changes['dataSource'] || changes['expandRequestId']) {
      this.tryExpandRequestedRow();
    }
  }

  private tryExpandRequestedRow(): void {
    if (this.expandRequestId == null) {
      return;
    }
    const row = this.dataSource?.data.find(r => r.id === this.expandRequestId);
    if (row && this.expandedElementId !== row.id) {
      this.toggleRow(row);
    }
  }

  getDate(dateStr: string): Date | null {
    if (dateStr == null) {
      return null;
    }
    return new Date(dateStr);
  }

  getStatusLabel(status: DataExchangeRequestStatusDto): string {
    const key = DataExchangeRequestStatusDto[status];
    return `dataManagement.dataExchangeRequests.status.${key}`;
  }

  getStatusOptions(): { name: string; value: number }[] {
    const relevantStatuses = this.isClosed ? this.closedStatusValues : this.openStatusValues;
    return relevantStatuses.map(status => ({ name: this.translocoService.translate(this.getStatusLabel(status)), value: status }));
  }

  isStatusFilterCustomized(): boolean {
    const statusFilter = this.dataSource.filters.find(f => f.fieldName === DataExchangeRequestPaginationFields.Status);
    if (!statusFilter) {
      return false;
    }
    const defaults = this.isClosed ? this.closedStatus : this.openStatus;
    return statusFilter.values.length !== defaults.length || !defaults.every(value => statusFilter.values.includes(value));
  }

  getCaller(column: string): string {
    switch (column) {
      case 'id':
        return DataExchangeRequestPaginationFields.Id;
      case 'name':
        return DataExchangeRequestPaginationFields.Name;
      case 'counterpart':
        return this.isReceived ? DataExchangeRequestPaginationFields.Sender : DataExchangeRequestPaginationFields.Recipient;
      case 'subRequestCount':
        return DataExchangeRequestPaginationFields.Items;
      case 'createdAt':
        return DataExchangeRequestPaginationFields.CreatedAt;
      case 'status':
        return DataExchangeRequestPaginationFields.Status;
      case 'deadline':
        return DataExchangeRequestPaginationFields.Deadline;
      case 'deadlineState':
        return DataExchangeRequestPaginationFields.DeadlineState;
      case 'submissionState':
        return DataExchangeRequestPaginationFields.SubmissionState;
      case 'closingState':
        return DataExchangeRequestPaginationFields.ClosingState;
      default:
        return '';
    }
  }

  getDeadlineStateOptions(): { name: string; value: string }[] {
    return getDeadlineStateFilterOptions(key => this.translocoService.translate(key));
  }

  onPageChanged(event: PageEvent): void {
    this.dataSource.fetchData(event.pageIndex, event.pageSize);
  }

  onSortChanged(event: Sort): void {
    if (event.direction) {
      this.dataSource.sort(this.getCaller(event.active), event.direction === 'asc');
    } else {
      this.dataSource.removeSorting();
    }
  }

  toggleRow(row: DataExchangeRequestDto): void {
    this.expandedElementId = this.expandedElementId === row.id ? null : row.id;
    if (this.expandedElementId != null) {
      this.getSubRequestDataSource(row);
      if (row.unseenNotificationCount > 0) {
        const previousCount = row.unseenNotificationCount;
        row.unseenNotificationCount = 0;
        this.dataExchangeRequestApiService.markNotificationsSeen({ dataExchangeRequestId: row.id }, true).subscribe({
          next: () => this.dataExchangeRequestApiService.reloadUnseenDataExchangeRequests(true),
          error: () => {
            row.unseenNotificationCount = previousCount;
            this.cd.markForCheck();
          },
        });
      }
    }
  }

  getSubRequestDataSource(row: DataExchangeRequestDto): PaginationDataSource<DataExchangeSubRequestDto> {
    let ds = this.subRequestDataSourceCache.get(row.id);
    if (!ds) {
      ds = new PaginationDataSource<DataExchangeSubRequestDto>(query => this.dataExchangeRequestApiService.getSubRequestsByPage(query, true));
      this.subRequestDataSourceCache.set(row.id, ds);
    }
    return ds;
  }

  private refreshExpandedSubRequests(): void {
    if (this.expandedElement == null) {
      return;
    }
    this.subRequestDataSourceCache.get(this.expandedElement.id)?.refresh();

    if (this.expandedElement) {
      this.dataExchangeRequestApiService.getRequestById(this.expandedElement.id).subscribe(request => {
        const row = this.expandedElement;
        if (row) {
          const closedBefore = this.closedStatusValues.includes(row.status);

          row.status = request.status;
          row.deadline = request.deadline;
          row.deadlineState = request.deadlineState;
          row.submissionState = request.submissionState;
          row.closingState = request.closingState;
          row.submittedAt = request.submittedAt;
          row.closedAt = request.closedAt;
          applyRowState(row, this.isReceived);

          const closeAfter = this.closedStatusValues.includes(request.status);

          if (closedBefore != closeAfter) {
            this.dataReloadRequired.emit();
          }
        }
      });
    }
  }

  onCommentAdded(text: string, row: DataExchangeRequestDto): void {
    this.dataExchangeRequestApiService.addComment(row.id, text, true).subscribe({
      next: () => this.refreshExpandedSubRequests(),
      error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
    });
  }

  onWithdrawRequest(row: DataExchangeRequestDto): void {
    if (row.status === DataExchangeRequestStatusDto.Deleted) {
      return;
    }

    this.dataExchangeRequestApiService.withdrawDataExchangeRequest(row.id, true).subscribe({
      next: () => this.dataReloadRequired.emit(),
      error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
    });
  }

  onApproveRequest(row: DataExchangeRequestDto): void {
    this.dataExchangeRequestApiService.approveDataExchangeRequest(row.id, true).subscribe({
      next: () => this.dataReloadRequired.emit(),
      error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
    });
  }

  onEditDeadline(row: DataExchangeRequestDto): void {
    const data: EditDataExchangeRequestDeadlineDialogData = { requestId: row.id, deadline: row.deadline };
    const dialogRef = this.dialog.open(EditDataExchangeRequestDeadlineDialogComponent, getDialogConfig(data, '400px'));
    dialogRef.afterClosed().subscribe((result: EditDataExchangeRequestDeadlineDialogResult | undefined) => {
      if (result?.event !== DialogActions.CONFIRM) {
        return;
      }
      this.dataReloadRequired.emit();
    });
  }

  onReopenRequest(row: DataExchangeRequestDto): void {
    const data: ReopenSubRequestsDialogData = { requestId: row.id, requestName: row.name ?? null };
    const dialogRef = this.dialog.open(ReopenSubRequestsDialogComponent, getDialogConfig(data, '1200px'));
    dialogRef.afterClosed().subscribe((result: ReopenSubRequestsDialogResult | undefined) => {
      if (result?.event !== DialogActions.CONFIRM || !result.results) {
        return;
      }
      this.showReopenResult(result.results);
      this.dataReloadRequired.emit();
      this.subRequestDataSourceCache.get(row.id)?.refresh();
    });
  }

  onOpenReopenDecisions(row: DataExchangeRequestDto): void {
    const data: ReopenDecisionDialogData = { requestId: row.id, requestName: row.name ?? null, deadline: row.deadline };
    const dialogRef = this.dialog.open(ReopenDecisionDialogComponent, getDialogConfig(data, '1200px'));
    dialogRef.afterClosed().subscribe((result: ReopenDecisionDialogResult | undefined) => {
      if (result?.event !== DialogActions.CONFIRM) {
        return;
      }
      this.snackBar.open(
        this.translocoService.translate('dataManagement.dataExchangeRequests.reopen.toast.decided'),
        this.translocoService.translate('common.buttons.close'),
        { duration: 3000, panelClass: ['app-snackbar', 'app-success'] }
      );
      this.dataReloadRequired.emit();
      this.subRequestDataSourceCache.get(row.id)?.refresh();
    });
  }

  private showReopenResult(results: ReopenRequestResultDto[]): void {
    const reopened = results.filter(r => r.outcome === ReopenOutcomeDto.Reopened).length;
    const requested = results.filter(r => r.outcome === ReopenOutcomeDto.Requested).length;
    const parts: string[] = [];
    if (reopened > 0) {
      parts.push(this.translocoService.translate('dataManagement.dataExchangeRequests.reopen.toast.reopened', { count: reopened }));
    }
    if (requested > 0) {
      parts.push(this.translocoService.translate('dataManagement.dataExchangeRequests.reopen.toast.requested', { count: requested }));
    }
    this.snackBar.open(parts.join(', '), this.translocoService.translate('common.buttons.close'), {
      duration: 4000,
      panelClass: ['app-snackbar', 'app-success'],
    });
  }

  saveToDirectory(row: DataExchangeRequestDto, event: Event): void {
    event.stopPropagation();
    this.saveToDirectoryService.saveRequest(row.id).subscribe(() => this.subRequestDataSourceCache.get(row.id)?.refresh());
  }

  onNavigateSubRequest(sub: DataExchangeSubRequestDto): void {
    this.navigateToSubRequest(sub);
  }

  onOpenLocalCopy(sub: DataExchangeSubRequestDto): void {
    const datasetId = sub.savedDatasetId;
    if (datasetId == null) {
      return;
    }

    switch (sub.type) {
      case DataExchangeSubRequestTypeDto.PackagingUnit:
        this.packagingUnitNavigationService.navigateToPackagingUnit(datasetId);
        break;
      case DataExchangeSubRequestTypeDto.PackagingSystem:
        this.packagingSystemNavigationService.navigateToPackagingSystem(datasetId);
        break;
      case DataExchangeSubRequestTypeDto.MultiMaterial:
        this.materialNavigationService.navigateToMaterial(datasetId);
        break;
      case DataExchangeSubRequestTypeDto.PackagingComponent:
        this.navigateToLocalCopyComponent(sub, datasetId);
        break;
    }
  }

  private navigateToLocalCopyComponent(sub: DataExchangeSubRequestDto, datasetId: number): void {
    const componentTypeId = sub.packagingComponent?.packagingComponentTypeId;
    if (componentTypeId == null) {
      return;
    }

    const componentTypeName = this.componentTypeService.getComponentTypeNameById(componentTypeId);
    if (this.componentTypeService.isDecorationComponentTypeId(componentTypeId)) {
      this.componentNavigationService.navigateToDecoration(componentTypeName, datasetId);
    } else {
      this.componentNavigationService.navigateToComponent(componentTypeName, datasetId);
    }
  }

  onApproveSubRequest(sub: DataExchangeSubRequestDto): void {
    this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Closed);
  }

  onReopenSubRequest(sub: DataExchangeSubRequestDto): void {
    this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.ReOpened);
  }

  onWithdrawSubRequest(sub: DataExchangeSubRequestDto): void {
    this.setSubRequestStatus(sub, DataExchangeRequestStatusDto.Deleted);
  }

  onShowSubRequestComments(sub: DataExchangeSubRequestDto): void {
    this.openSubRequestCommentsDialog(sub);
  }

  private setSubRequestStatus(sub: DataExchangeSubRequestDto, newStatus: DataExchangeRequestStatusDto): void {
    if (newStatus === DataExchangeRequestStatusDto.ReOpened) {
      this.openReopenSubRequestDialog(sub);
      return;
    }

    const dto: UpdateDataExchangeRequestStatusDto = { status: newStatus };
    this.dataExchangeRequestApiService.updateSubRequestStatus(sub.id, dto, true).subscribe({
      next: () => {
        this.refreshExpandedSubRequests();
      },
      error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
    });
  }

  private navigateToSubRequest(subRequest: DataExchangeSubRequestDto): void {
    let path: string;
    switch (subRequest.type) {
      case DataExchangeSubRequestTypeDto.PackagingUnit:
        path = PathResolveService.getDataExchangeRequestPackagingUnitPath(true, subRequest.id);
        break;
      case DataExchangeSubRequestTypeDto.PackagingSystem:
        path = PathResolveService.getDataExchangeRequestPackagingSystemPath(true, subRequest.id);
        break;
      case DataExchangeSubRequestTypeDto.PackagingComponent:
        path = this.getComponentSubRequestPath(subRequest);
        break;
      case DataExchangeSubRequestTypeDto.MultiMaterial:
        path = PathResolveService.getDataExchangeRequestMaterialPath(true, subRequest.id);
        break;
      default:
        return;
    }
    this.router.navigate([path]);
  }

  private getComponentSubRequestPath(subRequest: DataExchangeSubRequestDto): string {
    switch (subRequest.packagingComponent?.packagingComponentCategoryId) {
      case PackagingComponentTypesEnum.Closure:
        return PathResolveService.getDataExchangeRequestClosurePath(true, subRequest.id);
      case PackagingComponentTypesEnum.Decoration: {
        const decoType = this.componentTypeService.getComponentTypeNameById(subRequest.packagingComponent?.packagingComponentTypeId);
        return PathResolveService.getDataExchangeRequestDecorationPath(true, decoType, subRequest.id);
      }
      case PackagingComponentTypesEnum.PackagingAid:
        return PathResolveService.getDataExchangeRequestPackagingAidPath(true, subRequest.id);
      case PackagingComponentTypesEnum.Inlay:
        return PathResolveService.getDataExchangeRequestInlayPath(true, subRequest.id);
      default:
        return PathResolveService.getDataExchangeRequestMainBodyPath(true, subRequest.id);
    }
  }

  private openSubRequestCommentsDialog(sub: DataExchangeSubRequestDto): void {
    const title = this.translocoService.translate('dataManagement.dataExchangeRequests.comments.subRequestTitle');
    const data: SubRequestCommentsDialogData = { comments: sub.comments, title };
    const dialogRef = this.dialog.open(SubRequestCommentsDialogComponent, getDialogConfig(data, '500px'));
    dialogRef.afterClosed().subscribe((comment: string | undefined) => {
      if (comment) {
        this.dataExchangeRequestApiService.addSubRequestComment(sub.id, comment, true).subscribe({
          next: () => {
            this.refreshExpandedSubRequests();
          },
          error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
        });
      }
    });
  }

  private openReopenSubRequestDialog(sub: DataExchangeSubRequestDto): void {
    const title = this.translocoService.translate('dataManagement.dataExchangeRequests.comments.subRequestTitle');
    const data: SubRequestCommentsDialogData = {
      comments: sub.comments,
      title,
      allowOptionalComment: true,
      confirmButtonTranslationKey: 'dataManagement.dataExchangeRequests.buttons.reopenSubRequest',
    };
    const dialogRef = this.dialog.open(SubRequestCommentsDialogComponent, getDialogConfig(data, '500px'));
    dialogRef.afterClosed().subscribe((result: SubRequestCommentsDialogResult | undefined) => {
      if (!result?.confirmed) {
        return;
      }

      const dto: UpdateDataExchangeRequestStatusDto = { status: DataExchangeRequestStatusDto.ReOpened };
      this.dataExchangeRequestApiService.updateSubRequestStatus(sub.id, dto, true).subscribe({
        next: () => {
          if (result.comment) {
            this.dataExchangeRequestApiService.addSubRequestComment(sub.id, result.comment, true).subscribe({
              next: () => {
                this.dataReloadRequired.emit();
                this.refreshExpandedSubRequests();
              },
              error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
            });
            return;
          }

          this.dataReloadRequired.emit();
          this.refreshExpandedSubRequests();
        },
        error: error => showApiErrorDialog(this.dialog, error, this.translocoService)
      });
    });
  }
}
